import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import CategoryTrackMappingAPIs from "../../apiRoutes/CategoryTrackMapping";
import { GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY, GET_CTM_OPTIONS_QUERY_KEY } from "../../utils/constants/queryKeys";
import SectionHeader from "../common/SectionHeader";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import useAmsConfig from "../../hooks/useAmsConfig";
import CtmEditModal from "./Modals/CtmEditModal";
import BulkUploadCTMModal from "./Modals/BulkUploadCTMModal";
import CtmRow from "./CtmRow";
import CtmCampaignsViewModal from "./Modals/CtmCampaignsViewModal";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import toast from "react-hot-toast";

const LIMIT = 20;

const CategoryTrackMappingSection = () => {
  const [modal, setModal] = useState({ open: false, editData: null });
  const [viewCampaignsFor, setViewCampaignsFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const cfg = useAmsConfig();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSION_CODES.CATEGORY_TRACK_MAPPING_MANAGE);

  const { data: result, isLoading } = useQuery({
    queryKey: [...GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY, page, search],
    queryFn: () =>
      CategoryTrackMappingAPIs.list({
        axiosPrivate,
        search: search || undefined,
        limit: LIMIT,
        offset: page * LIMIT,
      }),
    placeholderData: keepPreviousData,
  });

  const mappings = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  const { mutate: remove, isPending: isDeleting } = useMutation({
    mutationFn: (id) => CategoryTrackMappingAPIs.remove({ axiosPrivate, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_CATEGORY_TRACK_MAPPINGS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_CTM_OPTIONS_QUERY_KEY });
      toast.success("Mapping deleted.");
    },
    onError: (e) => toast.error(e?.response?.data?.error_message || e?.message),
  });

  useEffect(() => setPage(0), [search]);

  const handleDelete = (row) => {
    if (!window.confirm(`Delete mapping "${row.track}" (${row.app_name} / ${row.language || "—"})?`)) return;
    remove(row.id);
  };

  if (isLoading) {
    return (
      <section className="p-4 md:p-6">
        <SectionHeader title="Category Track Mapping" className="mb-4" />
        <div className="flex items-center justify-center min-h-[320px] gap-2">
          <ButtonLoader color="#7f7ad5" />
          <span className="text-gray-600 font-medium">Loading mappings…</span>
        </div>
      </section>
    );
  }

  return (
    <section className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <SectionHeader title="Category Track Mapping" />
        {canManage && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              Bulk upload
            </Button>
            <Button onClick={() => setModal({ open: true, editData: null })}>Add</Button>
          </div>
        )}
      </div>

      <div className="flex flex-col min-h-[calc(100vh-200px)] rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-gray-200">
          <Input
            placeholder="Search category / track…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-[260px]"
          />
        </div>
        <div className="overflow-x-auto flex-1 min-h-0">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">App</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Language</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Category</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Track</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Track #</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Campaigns</th>
                {canManage && (
                  <th className="py-4 px-4 text-sm font-semibold text-gray-600 w-28">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {mappings.length > 0 ? (
                mappings.map((r) => (
                  <CtmRow
                    key={r.id}
                    row={r}
                    languageLabel={r.language ? cfg.languageMapping[r.language] || r.language : "—"}
                    canManage={canManage}
                    isDeleting={isDeleting}
                    onEdit={() => setModal({ open: true, editData: r })}
                    onDelete={() => handleDelete(r)}
                    onViewCampaigns={() => setViewCampaignsFor(r)}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="py-12 text-center text-gray-400 italic">
                    No mappings found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30 flex-shrink-0">
          <span className="text-sm text-muted-foreground">
            Showing {mappings.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + mappings.length, totalCount)} of {totalCount}
          </span>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page * LIMIT + mappings.length >= totalCount}
              onClick={() => setPage((p) => p + 1)}
              className="h-8"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {modal.open && (
        <CtmEditModal
          editData={modal.editData}
          onClose={() => setModal({ open: false, editData: null })}
        />
      )}
      {bulkOpen && <BulkUploadCTMModal onClose={() => setBulkOpen(false)} />}
      {viewCampaignsFor && (
        <CtmCampaignsViewModal ctm={viewCampaignsFor} onClose={() => setViewCampaignsFor(null)} />
      )}
    </section>
  );
};

export default CategoryTrackMappingSection;
